import React, { useState, useEffect, useRef } from 'react';
import './SalesPage06.css';
import '../../components/HeroSection.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import HeroSection from '../../components/HeroSection';
import InlineCheckoutForm from '../../components/InlineCheckoutForm/InlineCheckoutForm';
import { SparkleButton } from '../../components/SparkleEffect';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import { buildSalesLeadCheckoutParams } from '../../utils/salesLeadCheckoutParams';
import useSalesPromptRouting from '../../hooks/useSalesPromptRouting';
import { buildAuthOverlayRouteState } from '../../utils/authOverlayNavigation';

export default function SalesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    showLoginHint,
    loginReturnTo,
    getLeadSuccessPath,
  } = useSalesPromptRouting({ pageVariant: 'sales06' });

  const [newHeadlineIndex] = useState(3); // Use fourth headline variant (Chat with AI)

  // Ref for the checkout section (used for CTA scroll/focus behavior)
  const checkoutSectionRef = useRef(null);

  // Set experiment context when this page loads directly (e.g., /sales06 URL)
  // This ensures analytics and tracking have the correct experiment info
  // Note: When accessed via SalesPageWithABTest, that component sets the context instead
  useEffect(() => {
    // Only set context if not already set by SalesPageWithABTest
    if (!window.__experimentContext?.experiment_name?.includes('sales_page_design')) {
      const experimentContext = {
        experiment_name: 'sales_page_design_abc_test',
        experiment_variant: 'sales06',
        $feature_flag_sales_page_design_variant: 'sales06',
      };
      window.__experimentContext = experimentContext;
    }
  }, []);

  useEffect(() => {
    // Enhanced page view tracking with UTM data
    const utmDataForPageView = getUtmParamsForApi();
    
    pushEvent({
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page: 'sales_page_06',
      source: location.pathname,
      page_title: document.title,
      page_location: window.location.href,
      variant: 'test', 
      experiment: 'sales_page_ab_test',
      $feature_flag_sales_page_variant_06: 'nate06', // PostHog experiment variant
      ...utmDataForPageView
    });
  }, [location]);



  // Define arrays for hero titles and highlighted texts
  const heroTitles = [
    "Create Profitable Trading Bots ", // Index 0 (Original/Control version)
    "Build Powerful, Automated Trading Bots in Minutes ", // Index 1 (New/Variant version)
    "Ditch Endless Coding. Build an AI Trading Bot in minutes.", // Index 2
    "Chat with AI to build a custom trading bot in minutes." // Index 3 (Chat with AI emphasis)
  ];

  const heroHighlightedTexts = [
    "Without Writing a Single Line of Code", // Index 0 (Original/Control version)
    "No Coding Required!", // Index 1 (New/Variant version)
    "No Coding Needed.", // Index 2
    "No Coding Required." // Index 3 (consistent with others)
  ];

  const heroTitle = heroTitles[newHeadlineIndex] || heroTitles[0];
  const heroHighlightedText = heroHighlightedTexts[newHeadlineIndex] || heroHighlightedTexts[0];

  const handleLoginClick = () => {
    pushEvent({
      event: ANALYTICS_EVENTS.LOGIN_CLICKED,
      method: 'sales_page_06_link',
      source: location.pathname
    });
    navigate(`/login?returnTo=${encodeURIComponent(loginReturnTo)}`, {
      state: buildAuthOverlayRouteState(location),
    });
  };

  // Handle lead capture success without putting auto-login tokens in URLs.
  const handleContactSuccess = ({ contactData, leadTransactionId, autoLoginToken, howFoundUs, howFoundUsOtherText, existingUser, redirectTo }) => {
    if (existingUser && redirectTo) {
      navigate(redirectTo);
      return;
    }

    // Build query params for prompt page
    const params = buildSalesLeadCheckoutParams({
      contactData,
      leadTransactionId,
      variant: 'sales06',
      howFoundUs,
      howFoundUsOtherText,
    });

    navigate(getLeadSuccessPath(params, autoLoginToken));
  };

  const logoImages = [
    { src: "/images/logos/interactive-brokers.png", alt: "Interactive Brokers", width: "240", height: "32" }, 
    { src: "/images/logos/kucoin.png", alt: "Kucoin", width: "160", height: "32" },
    { src: "/images/logos/alpaca.png", alt: "Alpaca", width: "80", height: "32" }, 
    { src: "/images/logos/tradier.png", alt: "Tradier", width: "70", height: "32" }, 
    { src: "/images/logos/coinbase.png", alt: "Coinbase Pro", width: "200", height: "32" }, 
    { src: "/images/logos/binance.png", alt: "Binance", width: "170", height: "32" },
    { src: "/images/logos/schwab.png", alt: "Charles Schwab", width: "70", height: "32" },
    { src: "/images/logos/bitunix.png", alt: "BitUnix", width: "150", height: "32" },
    { src: "/images/logos/kraken.png", alt: "Kraken", width: "220", height: "32" },
    
  ];
  

  // Benefits data variants for A/B testing
  const benefitsDataVariants = {
    // Original variant
    benefitA: [
      {
        icon: 1,
        title: "Choose your bot type",
        description: "Grab a ready-made bot or chat with our AI to craft your own."
      },
      {
        icon: 2,
        title: "AI Codes & Back-tests",
        description: "BotSpot writes the code, runs a historical test, and shows win-rate & drawdown."
      },
      {
        icon: 3,
        title: "Go Live or Paper",
        description: "Hook up your broker and let the bot trade automatically."
      }
    ],
    // Option A - Focus on AI Agent conversation flow
    benefitB: [
      {
        icon: 1,
        title: "Create & Visualize",
        description: "Describe your strategy in plain English. AI generates the code and a visual flowchart so you understand exactly how it works."
      },
      {
        icon: 2,
        title: "Backtest & Refine",
        description: "Ask the AI to backtest against real market data. See the results, then chat to improve performance."
      },
      {
        icon: 3,
        title: "Deploy & Profit",
        description: "Connect your broker, choose paper or live trading, and watch your bot execute trades automatically."
      }
    ],
    // Option B - Emphasize conversational/easy nature (aligned with video)
    benefitC: [
      {
        icon: 1,
        title: "Describe Your Idea",
        description: "Tell the AI what you want. It writes the code and shows you a visual flowchart of your strategy."
      },
      {
        icon: 2,
        title: "Test & Improve",
        description: "One click to backtest. Chat with AI to tweak and optimize until you're satisfied."
      },
      {
        icon: 3,
        title: "Go Live",
        description: "Pick your broker, start with paper trading or real money, and let your bot trade 24/7."
      }
    ],
    // Option C - More action-oriented
    benefitD: [
      {
        icon: 1,
        title: "Build in Seconds",
        description: "Describe your strategy, get working code plus a visual flowchart instantly."
      },
      {
        icon: 2,
        title: "Backtest & Iterate",
        description: "Run historical tests, review performance, and refine through conversation."
      },
      {
        icon: 3,
        title: "Trade Hands-Free",
        description: "Connect your broker and deploy. Your bot trades automatically while you watch."
      }
    ]
  };

  // Select which benefits variant to use (change this for A/B testing)
  const benefitsData = benefitsDataVariants.benefitC;

  // CTA button click handler - scrolls on mobile, focuses input on desktop
  const handleCtaClick = () => {
    const MOBILE_BREAKPOINT = 992;
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

    if (isMobile) {
      // Mobile: smooth scroll to checkout section
      checkoutSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Desktop: focus the name input field to trigger hover/focus effect
      const nameInput = checkoutSectionRef.current?.querySelector('input[name="name"], input[type="text"]');
      if (nameInput) {
        nameInput.focus();
        // Scroll into view if not fully visible
        nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    // Track CTA click
    pushEvent({
      event: 'cta_click',
      location: 'hero_section',
      action: isMobile ? 'scroll_to_checkout' : 'focus_checkout_input',
      source: 'sales_page_06'
    });
  };

  return (
    <div className="sales-page">
      {/* Hero Section and Inline Checkout Form */}
      <div className="sales-page-hero-and-form-container">
        <div className="sales-page-hero-wrapper">
          <HeroSection
            title={heroTitle}
            highlightedTitleText={heroHighlightedText}
            subtitle="Describe your strategy in plain English. BotSpot writes, back-tests & deploys it for you."
            ctaText="Start Your Free Trial"
            onCtaClick={handleCtaClick}
            customerCount="2,300+"
            logoImages={logoImages}
            benefitsData={benefitsData}
            benefitsSectionTitle="Launch a Bot in 3 Steps"
          >
            {/* Video Section - Rendered INSIDE HeroSection between CTA and benefits */}
            <div className="sales-page-video-section">
              <h2 className="hero-title">See It in Action</h2>
              <p className="hero-subtitle">Watch how easy it is to create your first trading bot</p>
              <div className="sales-page-video-container">
                <iframe
                  src="https://www.youtube.com/embed/TE75ytXCAPA?autoplay=1&mute=1&loop=1&playlist=TE75ytXCAPA"
                  title="BotSpot AI Bot Builder Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="sales-page-video-cta">
                <SparkleButton className="sparkle-button-cta" onClick={handleCtaClick}>
                  Start Your Free Trial
                </SparkleButton>
              </div>
            </div>
          </HeroSection>
        </div>

        {/* Inline Checkout Form */}
        <div className="sales-page-inline-checkout-form" ref={checkoutSectionRef}>
          <div className="sales-page-inline-form-content">
            {/* Lead capture form - no payment, forwards to sales-ai-prompt */}
            {showLoginHint && (
              <div className="sales-login-hint">
                Already a member?
                <button type="button" className="sales-login-link" onClick={handleLoginClick}>
                  Log in
                </button>
              </div>
            )}
            <InlineCheckoutForm
              onContactSuccess={handleContactSuccess}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
